import React, { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AuthShell, ErrorBox, Field, PasswordRules, ghostBtn, inputCls, passwordProblems, primaryBtn } from "@/components/auth/AuthUi";

// Shown instead of the whole application while the backend reports password_change_required.
// The server enforces the same rule: every other endpoint answers 403 until this is done.
export default function ForcePasswordChange() {
  const { user, setSession, logout } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const blocked = passwordProblems(next, confirm, user?.email).length > 0 || !current;

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const { data } = await api.post("/auth/change-password", { current_password: current, new_password: next });
      setSession(data);
      toast.success("Password updated");
    } catch (e2) {
      setErr(apiError(e2));
    } finally { setBusy(false); }
  };

  return (
    <AuthShell title="Set a new password" testId="force-password-change"
      subtitle={`Signed in as ${user?.email}. You must choose a new password before continuing.`}>
      <form onSubmit={submit} className="space-y-4">
        <Field label="Current password">
          <input data-testid="force-current" type="password" autoComplete="current-password" value={current}
            onChange={(e) => setCurrent(e.target.value)} className={inputCls} />
        </Field>
        <Field label="New password">
          <input data-testid="force-new" type="password" autoComplete="new-password" value={next}
            onChange={(e) => setNext(e.target.value)} className={inputCls} />
        </Field>
        <Field label="Confirm new password">
          <input data-testid="force-confirm" type="password" autoComplete="new-password" value={confirm}
            onChange={(e) => setConfirm(e.target.value)} className={inputCls} />
        </Field>
        <PasswordRules password={next} confirm={confirm} email={user?.email} />
        <ErrorBox testId="force-error">{err}</ErrorBox>
        <div className="flex gap-2">
          <button type="submit" disabled={busy || blocked} data-testid="force-submit" className={`flex-1 ${primaryBtn}`}>
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Update password
          </button>
          <button type="button" onClick={logout} className={ghostBtn}>Sign out</button>
        </div>
      </form>
    </AuthShell>
  );
}
